import {
  FluidAlignment,
  FluidButtonConfig,
  FluidButtonType,
  FluidContextMenuSection,
  FluidContextMenuToggle,
  FluidFilterSet,
  FluidFormElementState,
  FluidIconConfig,
  FluidIconKey,
  FluidMenuSectionStyle,
  FluidProgressState,
  FluidRowData,
  FluidSize,
  FluidSortOrder,
  FluidTableColumnAdjustments,
  FluidTableHeader,
  FluidTableHeaderType,
  FluidTableSelectionOptions,
  FluidTheme,
  FluidToggleConfig,
  FluidToggleType,
  getAtPath,
} from '@lmig/fluid-core';
import { FluidFormElementTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-form-element-translation-options.interface';
import { FluidTableTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-table-translation-options.type';
import { FluidButtonTranslations } from '@lmig/fluid-core/lib/i18n/types/temp-element-translations';
import { FluidDate } from '@lmig/fluid-core/lib/structs/types/fluid-date.type';
import { h } from '@stencil/core';

import {
  ClearSelectionButton,
  ContextMenuIconTrigger,
  FilterIcon,
  ToggleSelectAll,
} from '../../../common/fluid-common-icon.components';
import {
  CLEAR_SELECT_MESSAGE,
  DATE_RANGE_SLOT_SUFFIX,
  SEARCH_SLOT_SUFFIX,
  SELECTED,
} from '../../../constants/constants';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { themeVar } from '../../../utils/themes/theme.variables';
import { Utils } from '../../../utils/utils';
import { convertToDate } from '../../fluid-date-range/util/fluid-date-range.utils';
import { FluidTable, FluidTranslatableTableHeader } from '../fluid-table';
import {
  FILTER_BY_DATE_DEFAULT,
  SEARCH_FILTER_DEFAULT,
  SLOTTED_FILTER,
} from '../internal/filter/fluid-filter.constants';
import {
  createColumnFilters,
  createFilterMenuSections,
  processTableFilters,
  updateAppliedFilters,
  updateSelectedFilters,
} from '../internal/filter/fluid-table-filter.utils';
import {
  deselectAll,
  getSelectColumnWidth,
  isSelected,
  toggleSelectAll,
} from '../internal/fluid-table-select.utils';
import { getColumnVisibilityMenu } from '../internal/fluid-table-templating.utils';
import { TableFilterRef } from '../util/fluid-table-deprecation.utils';
import { hasSplitHeaders } from './fluid-table-structure.components';

export type TableFilterType =
  | HTMLFluidContextMenuElement
  | HTMLFluidComboBoxElement
  | HTMLFluidDateRangeElement;

export interface TableHeaderCellProps {
  theme?: FluidTheme;
  header?: FluidTranslatableTableHeader;
  headers?: FluidTranslatableTableHeader[];
  onFilterChange?: Function;
  disableActiveFiltering?: boolean;
  actionSortBy?: (
    query: string,
    direction: FluidSortOrder,
    isMultiSort?: boolean
  ) => void;
  dataItems?: FluidRowData[];
  setFilterRef?: (el: TableFilterRef) => void;
  colSpan?: number;
  rowSpan?: number;
  host?: FluidTable;
  splitLevel?: number;
  onApplyFilters?: () => void;
  onResetFilterMenu?: (header: FluidTableHeader) => void;
  persistedFilters?: string[];
  appliedFilters?: FluidFilterSet[];
  selectedFilters?: FluidFilterSet[];
  columnAdjustments?: FluidTableColumnAdjustments;
  allowOverflow?: boolean;
  stopPropagation?: boolean;
  alignment?: FluidAlignment;
  allowTabIndexCells?: boolean;
  stickyColumn?: boolean;
  selectionOptions?: FluidTableSelectionOptions;
  serverMode?: boolean;
  fluidTranslate?: FluidTableTranslationOptions;
  i18nStore?: FluidI18nStore;
}

async function handleFilterUpdates(
  menuSections: FluidContextMenuSection[],
  host: FluidTable,
  disableActiveFiltering: boolean,
  header: FluidTableHeader,
  onApplyFilters?: () => void
) {
  const start = performance.now();
  updateSelectedFilters(header, menuSections, host);
  updateAppliedFilters(header, menuSections, host);
  if (!disableActiveFiltering) {
    onApplyFilters();
  }
  Utils.logPerformance('handleFilterUpdates', start);
}

/**
 * Renders a filter icon, with context menu for filter toggles
 * @param props
 * @constructor
 */
export const Filter = (props: TableHeaderCellProps) => {
  const {
    header,
    theme,
    dataItems,
    disableActiveFiltering,
    onApplyFilters,
    onResetFilterMenu,
    setFilterRef,
    host,
    stopPropagation,
    serverMode,
    fluidTranslate,
    i18nStore,
    persistedFilters,
    appliedFilters,
  } = props;
  if (header.disableFilters) return;

  const compactFilters =
    host.allowOverflow ||
    header.filterSectionStyle === FluidMenuSectionStyle.INLINE;

  const searchSection = {
    id: `${header.dataPath}${SEARCH_SLOT_SUFFIX}`,
    title:
      i18nStore?.translate(fluidTranslate?.filters?.columnSearch?.title) ||
      SEARCH_FILTER_DEFAULT,
    type: SLOTTED_FILTER,
    toggles: createColumnFilters(
      header,
      dataItems,
      appliedFilters,
      persistedFilters
    ),
    slotName: `${header.dataPath}${SEARCH_SLOT_SUFFIX}`,
  } as FluidContextMenuSection;

  const dateRangeFilterSection = {
    id: `${header.dataPath}${DATE_RANGE_SLOT_SUFFIX}`,
    title:
      i18nStore?.translate(fluidTranslate?.filters?.dateRange?.title) ||
      FILTER_BY_DATE_DEFAULT,
    type: SLOTTED_FILTER,
    toggles: createColumnFilters(
      header,
      dataItems,
      appliedFilters,
      persistedFilters
    ),
    slotName: `${header.dataPath}${DATE_RANGE_SLOT_SUFFIX}`,
  } as FluidContextMenuSection;

  /**
   * Create the menu sections for this filter menu.
   */
  const menuSections = header.enableColumnSearch
    ? [searchSection]
    : header.type === FluidTableHeaderType.DATE
    ? [dateRangeFilterSection]
    : createFilterMenuSections(
        header,
        appliedFilters,
        dataItems,
        compactFilters,
        serverMode,
        fluidTranslate,
        i18nStore,
        persistedFilters
      );

  return (
    <ContextMenuIconTrigger
      doRender={!header.disableFilters}
      fluidTranslate={fluidTranslate?.filters?.columnFilters}
      menuId={header.dataPath}
      class='header-cell-filter-menu'
      alignment={FluidAlignment.LEFT}
      theme={theme}
      closeLabel={disableActiveFiltering ? 'Apply Filters' : 'Close'}
      setRef={(ref: HTMLFluidContextMenuElement) => {
        !header.enableColumnSearch && setFilterRef(ref);
      }}
      compactFilters={compactFilters}
      stopPropagation={stopPropagation}
      outsideClickClose
      displayMenuActions
      menuSections={menuSections}
      onContextMenuToggled={(menuToggled: any) => {
        if (disableActiveFiltering && !menuToggled) {
          onApplyFilters();
        }
      }}
      onLinkClicked={(linkClicked: { actionKey: string }) => {
        if (linkClicked.actionKey === 'resetFilters') {
          onResetFilterMenu(header);
        }
      }}
      onToggleChanged={(menuSections: FluidContextMenuSection[]) =>
        handleFilterUpdates(
          menuSections,
          host,
          disableActiveFiltering,
          header,
          onApplyFilters
        )
      }>
      {header.type === FluidTableHeaderType.DATE && (
        <DateRangeFilter
          enabled={header.type === FluidTableHeaderType.DATE}
          statefulFiltersEnabled={host.statefulOptions?.filters?.enabled}
          persistedFilters={persistedFilters}
          setFilterRef={setFilterRef}
          fluidTranslate={fluidTranslate?.filters?.['dateRange']}
          clearRangeFilter={() => {
            (host._filterMenus?.[header.dataPath] as HTMLFluidDateRangeElement)
              .reset()
              .then(() => {
                delete host._rangeFilter[header.dataPath];
                processTableFilters(host).then();
              });
          }}
          dataPath={header.dataPath}
          onDateRangeChange={(range) => {
            if (range.from && range.to) {
              host._rangeFilter[header.dataPath] = range;
              processTableFilters(host).then();
            }
          }}
          appliedFilters={appliedFilters}
          minDate={
            sortUniqueDatesAtPath(
              dataItems,
              header.dataPath,
              FluidSortOrder.ASC
            )[0]
          }
          maxDate={
            sortUniqueDatesAtPath(
              dataItems,
              header.dataPath,
              FluidSortOrder.DESC
            )[0]
          }
          dateRangeFilterSection={dateRangeFilterSection}
        />
      )}
      <ColumnSearch
        enabled={header.enableColumnSearch}
        setFilterRef={setFilterRef}
        searchSection={searchSection}
        appliedFilters={appliedFilters}
        persistedFilters={persistedFilters}
        handleFilterUpdates={(menuSections: FluidContextMenuSection[]) =>
          handleFilterUpdates(
            menuSections,
            host,
            disableActiveFiltering,
            header,
            onApplyFilters
          )
        }
      />
      <FilterIcon
        doRender={true}
        theme={theme}
        filtersActive={
          appliedFilters?.length > 0 || !!host._rangeFilter[header.dataPath]
        }
        class='header-icon'
        size={themeVar<FluidSize>(theme, 'icons.table_filter.size')}
      />
    </ContextMenuIconTrigger>
  );
};

/**
 * Parses all dates from dataItems at a specific dataPath to a unique set, before sorting
 * by date, in either ascending or descending order.
 * @param dataItems
 * @param dataPath
 * @param sortOrder
 */
export const sortUniqueDatesAtPath = (
  dataItems: FluidRowData[],
  dataPath: string,
  sortOrder: FluidSortOrder
) => {
  const dates: (string | number | Date)[] = Array.from(
    new Set<string>(dataItems.map((item) => getAtPath(item, dataPath)))
  );
  const ascComparator = (a: string, b: string) =>
    convertToDate(a) < convertToDate(b) ? -1 : 1;
  const descComparator = (a: string, b: string) =>
    convertToDate(a) < convertToDate(b) ? 1 : -1;
  return dates.sort((a: string, b: string) =>
    sortOrder === FluidSortOrder.ASC
      ? ascComparator(a, b)
      : descComparator(a, b)
  );
};

interface DateRangeFilterProps {
  enabled: boolean;
  setFilterRef: (
    ref:
      | HTMLFluidContextMenuElement
      | HTMLFluidComboBoxElement
      | HTMLFluidDateRangeElement
  ) => void;
  appliedFilters?: FluidFilterSet[];
  dateRangeFilterSection: FluidContextMenuSection;
  onDateRangeChange: (dateRange) => void;
  identifier?: string;
  minDate?: FluidDate;
  maxDate?: FluidDate;
  clearRangeFilter: (dataPath: string) => void;
  dataPath: string;
  statefulFiltersEnabled?: boolean;
  persistedFilters?: any;
  fluidTranslate?: {
    minDate?: FluidFormElementTranslationOptions;
    maxDate?: FluidFormElementTranslationOptions;
    clear?: FluidButtonTranslations;
  };
}
export function DateRangeFilter(props: DateRangeFilterProps) {
  if (!props.enabled) return;
  const {
    dateRangeFilterSection,
    setFilterRef,
    identifier,
    onDateRangeChange,
    maxDate,
    minDate,
    clearRangeFilter,
    dataPath,
    statefulFiltersEnabled,
    persistedFilters,
    fluidTranslate,
  } = props;
  const { slotName } = dateRangeFilterSection;

  return (
    <fluid-container
      width={400}
      slot={slotName}
      textAlign={FluidAlignment.RIGHT}
      data-test-id={`${identifier}-date-range-container`}>
      <fluid-date-range
        ref={setFilterRef}
        identifier={identifier}
        initialValue={statefulFiltersEnabled && persistedFilters}
        minDate={minDate}
        emitInitialValue={true}
        maxDate={maxDate}
        fluidTranslate={fluidTranslate}
        onValueChange={(dateChangedEvent: CustomEvent<any>) => {
          onDateRangeChange(dateChangedEvent.detail);
        }}></fluid-date-range>
      <fluid-button
        small={true}
        fluidTranslate={fluidTranslate?.clear}
        label={'Clear'}
        type={FluidButtonType.GHOST}
        removeMargin={true}
        onClick={() => clearRangeFilter(dataPath)}
      />
    </fluid-container>
  );
}

/**
 * Creates the column search menu.
 */
interface ColumnSearchProps {
  enabled: boolean;
  setFilterRef: (
    ref: HTMLFluidContextMenuElement | HTMLFluidComboBoxElement
  ) => void;
  appliedFilters?: FluidFilterSet[];
  persistedFilters?: any;
  searchSection: FluidContextMenuSection;
  handleFilterUpdates: (menuSections: FluidContextMenuSection[]) => void;
  identifier?: string;
}
export function ColumnSearch(props: ColumnSearchProps) {
  if (!props.enabled) return;
  const {
    searchSection,
    handleFilterUpdates,
    setFilterRef,
    identifier,
    persistedFilters,
  } = props;
  const { slotName, toggles } = searchSection;
  const options = toggles.map((toggle: FluidContextMenuToggle) => ({
    label: toggle.label,
    value: toggle.normalisedValue,
    selected: persistedFilters?.indexOf(toggle.id) === -1,
  }));
  return (
    <fluid-container
      width={400}
      slot={slotName}
      data-test-id={`${identifier}-search-container`}>
      <fluid-combo-box
        ref={(el) => setFilterRef(el)}
        multiSelect={true}
        onValueChange={(elementState: CustomEvent<FluidFormElementState>) => {
          const { value } = elementState.detail;
          handleFilterUpdates([invertSearchSelection(value, searchSection)]);
        }}
        options={options}
      />
    </fluid-container>
  );
}

/**
 * Inverts the search selection so the user can select what to include, but the menuSections that
 * get passed to updating filters maintain their current format so we don't have to change the
 * filtering process.
 * @param selectedValues
 * @param menuSection
 */
function invertSearchSelection(
  selectedValues = [],
  menuSection: FluidContextMenuSection
) {
  return {
    ...menuSection,
    toggles: menuSection.toggles.map((toggle: FluidContextMenuToggle) => ({
      ...toggle,
      selected:
        selectedValues?.length > 0
          ? selectedValues.indexOf(toggle.normalisedValue) !== -1
          : true,
    })),
  };
}

type ColumnFilterProps = {
  renderColumn: boolean;
  renderFilter: boolean;
  headers: FluidTableHeader[];
  rowActions;
  onColumnFiltersChanged: Function;
  rowSpan?: number;
  splitHeaders?: boolean;
  buttonConfig: FluidButtonConfig;
  theme?: FluidTheme;
  i18nStore?: FluidI18nStore;
  fluidTranslate?: FluidTableTranslationOptions;
  identifier?: string;
};
/**
 * Renders the column filter icon and context menu
 * @param props
 * @constructor
 */
// columnVisibility
export const ColumnFilter = (props: ColumnFilterProps) => {
  const {
    headers,
    rowActions,
    onColumnFiltersChanged,
    rowSpan,
    splitHeaders,
    renderColumn,
    renderFilter,
    buttonConfig,
    theme,
    i18nStore,
    fluidTranslate,
    identifier,
  } = props;
  const menuSections: FluidContextMenuSection[] = [
    getColumnVisibilityMenu(headers, i18nStore, fluidTranslate),
  ];

  const actionColumnWidth = { 'min-width': `${rowActions?.length || 0}em` };
  const actionColumnClass = {
    'table-header-cell action-column': true,
    'column-visibility': true,
    clickable: true,
    'with-split-points': splitHeaders,
  };
  let contextMenuRef;

  const icon: FluidIconConfig = {
    key: FluidIconKey.ELLIPSIS_VERTICAL,
    size: FluidSize.SMALL,
    theme,
    overrideGlobalTheme: !!theme,
  };

  const _defaultButtonConfig: FluidButtonConfig = {
    icon,
  };

  // allow user to change the default values
  const _config: FluidButtonConfig = {
    ..._defaultButtonConfig,
    ...buttonConfig,
    inheritIconColor: true,
    identifier,
  };

  // prevent the user from changing the size of the icon
  _config.icon.size = FluidSize.SMALL;
  _config.small = true;

  return (
    renderColumn && (
      <th
        class={actionColumnClass}
        style={actionColumnWidth}
        rowSpan={rowSpan || 1}
        onClick={(e) => {
          e.stopImmediatePropagation();
          contextMenuRef?.toggleMenu();
        }}>
        {renderFilter && (
          <fluid-context-menu
            class='column-visibility-menu'
            menuId='table-context'
            fluidTranslate={fluidTranslate?.columnVisibility}
            menuSections={menuSections}
            menuAlignment={FluidAlignment.RIGHT}
            ref={(el) => (contextMenuRef = el)}
            onToggleChanged={($event) => onColumnFiltersChanged($event.detail)}
            onClick={(e) => e.stopImmediatePropagation()}
            theme={theme}
            overrideGlobalTheme={!!theme}
            outsideClickClose={true}
            displayMenuActions={true}
            asWrapper={true}>
            <fluid-button
              class='column-visibility-trigger'
              config={_config}
              removeMargin={true}
              data-test-id={`${_config.identifier}-col-visibility-trigger`}
              identifier={identifier}
            />
          </fluid-context-menu>
        )}
      </th>
    )
  );
};

/**
 * Returns "toggled" Prop value for the "Select All" Toggle.
 */
const getToggleAllValue = (
  selectableDataPath: string,
  dataItems: FluidRowData[],
  toggleType: FluidToggleType
) => {
  const isSelected = (dataItem) =>
    selectableDataPath ? dataItem[selectableDataPath] : dataItem[SELECTED];
  switch (true) {
    case dataItems.every((dataItem) => isSelected(dataItem)):
      return true;
    case !dataItems.some((dataItem) => isSelected(dataItem)):
      return false;
    default:
      return toggleType === FluidToggleType.CHECKBOX
        ? FluidProgressState.INDETERMINATE
        : false;
  }
};

/**
 * Renders selectable column header
 * @param host
 * @param selectionOptions
 * @param headers
 * @constructor
 */
export const AddSelectHeader = (
  host: FluidTable,
  selectionOptions: FluidTableSelectionOptions,
  headers: HTMLElement[],
  i18nStore?: FluidI18nStore
) => {
  const {
    selectableHeaderLabel,
    selectableDataPath,
    columnAdjustments,
    _dataSource,
    _headers,
    _tableDisabled,
  } = host ?? {};
  const {
    selectable,
    mutuallyExclusive,
    selectAllDisabled,
    toggleType,
    toggleConfig,
  } = selectionOptions ?? {};

  // Short-circuit early if Table does not have select column
  if (!host.selectable && !selectable) return headers;

  let toggleRef;

  const toggleAllValue = getToggleAllValue(
    selectableDataPath,
    _dataSource,
    toggleType
  );

  const handleToggleAllClick = () => {
    if (!toggleRef || _tableDisabled) return;
    const toggleValue = !toggleRef.toggled;
    toggleSelectAll(host, toggleValue);
    toggleRef.toggled = toggleValue;
  };

  const clearSelection = () => {
    if (_tableDisabled) return;
    deselectAll(host);
  };

  const toggleConfigProps: FluidToggleConfig = {
    ...toggleConfig,
    toggleType,
  };

  const _hasSplitHeaders = hasSplitHeaders(_headers);
  const _hasStickyColumn = !_hasSplitHeaders && !!host._stickyColumn;

  // compute toggle-column width
  const colWidth = getSelectColumnWidth(
    selectionOptions,
    _hasStickyColumn,
    columnAdjustments?.selectable
  );

  const cell = (
    <th
      onClick={() => handleToggleAllClick()}
      style={
        !!colWidth && {
          width: colWidth,
          minWidth: colWidth,
          maxWidth: colWidth,
        }
      }
      class={{
        'table-header-cell toggle-column': true,
        'with-split-points': _hasSplitHeaders,
        'sticky-column': _hasStickyColumn,
        'fixed-width': !!colWidth,
        clickable: !selectAllDisabled,
        disabled: _tableDisabled,
      }}>
      {!!selectableHeaderLabel && (
        <div class='toggle-column-label'>
          <span class='toggle-column-label-text'>{selectableHeaderLabel}</span>
        </div>
      )}
      {!selectAllDisabled &&
        (mutuallyExclusive ? (
          <ClearSelectionButton
            onClicked={() => clearSelection()}
            disabled={
              !_dataSource.some((dataItem) => isSelected(host, dataItem))
            }
            popoverMessage={
              i18nStore?.translate(
                host.fluidTranslate?.helpText?.clearSelection
              ) || CLEAR_SELECT_MESSAGE
            }
          />
        ) : (
          <ToggleSelectAll
            toggleConfigProps={toggleConfigProps}
            disabled={_tableDisabled}
            toggleAllValue={toggleAllValue}
            toggleType={toggleType}
            toggleRef={(el: HTMLFluidToggleElement) => (toggleRef = el)}
          />
        ))}
    </th>
  );

  return [cell, ...headers];
};
